/**
 * MultiAgentWorkspacePanel — dev-agent "Workspace" parity for the multi-agent IDE.
 *
 * Lists the code-bearing artifacts the run produced (apex classes/triggers +
 * refactor diffs), with a R/C/M badge, click-to-open-in-editor, and a local
 * clear. Reads useMultiAgentStore.artifacts (which persist across runs now), so
 * the workspace accumulates context until the user clears it.
 */
import { useMemo, useState } from "react";
import { Package } from "lucide-react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { sanitizeArtifactLabel } from "@/services/multi-agent/artifactDisplay";
import toast from "react-hot-toast";
import { getArtifact, isWhitespace, isWordChar } from "@/services/multi-agent";
import { extractArtifactCode } from "@/pages/multi-agent/ide/useMultiAgentEditorBridge";
import { PanelSection } from "./PanelSection";
import { CODE_ARTIFACT_TYPES } from "@/constants/multi-agent/artifact.constants";

/** Canonical UUID (8-4-4-4-12 hex). Only files with a real backend id are shown
 *  + openable; anything without one is filtered out of the Workspace entirely. */
function _isBackendUuid(id: string): boolean {
  if (!id || id.length !== 36) return false;
  for (let i = 0; i < 36; i++) {
    const ch = id[i];
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      if (ch !== "-") return false;
    } else if (
      !((ch >= "0" && ch <= "9") || (ch >= "a" && ch <= "f") || (ch >= "A" && ch <= "F"))
    ) {
      return false;
    }
  }
  return true;
}

const _isAsciiLetter = (ch: string): boolean =>
  (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z");
/** Chars allowed in a leading prefix segment: letters, "_", or ".". */
const _isPrefixSegmentChar = (ch: string): boolean =>
  _isAsciiLetter(ch) || ch === "_" || ch === ".";

/**
 * Strip, once from the start, a leading prefix matching EITHER:
 *   (a) one or more of [letter/"_"/"."] followed by ":"  (e.g. `auto_fix:`, `report.x:`)
 *   (b) "export" + whitespace + word chars + ":" + optional whitespace  (e.g. `export PDF: `)
 * Form (a) is tried first; if neither matches the leading segment, the string is
 * returned unchanged.
 */
function _stripPrefix(s: string): string {
  // (a) prefix-segment chars then ":"
  let i = 0;
  while (i < s.length && _isPrefixSegmentChar(s[i])) i++;
  if (i > 0 && i < s.length && s[i] === ":") {
    return s.slice(i + 1);
  }

  // (b) "export" whitespace word ":" optional-whitespace (case-insensitive "export")
  if (s.slice(0, 6).toLowerCase() === "export") {
    let j = 6;
    const wsStart = j;
    while (j < s.length && isWhitespace(s[j])) j++;
    if (j > wsStart) {
      const wordStart = j;
      while (j < s.length && isWordChar(s[j])) j++;
      if (j > wordStart && j < s.length && s[j] === ":") {
        j++; // consume ":"
        while (j < s.length && isWhitespace(s[j])) j++; // trailing whitespace
        return s.slice(j);
      }
    }
  }

  return s;
}

/**
 * Strip a trailing `.cls`/`.trigger` (case-insensitive) only. This is narrower
 * than the shared `bareClassName` (which also strips `.page`): here a trailing
 * `.page` is deliberately left intact.
 */
function _stripExt(s: string): string {
  const dot = s.lastIndexOf(".");
  if (dot < 0) return s;
  const ext = s.slice(dot + 1).toLowerCase();
  if (ext === "cls" || ext === "trigger") return s.slice(0, dot);
  return s;
}

function _bareName(raw: string): string {
  return _stripExt(_stripPrefix(sanitizeArtifactLabel(raw, "")))
    .split("\n")[0]
    .trim();
}

const _BADGE = {
  R: { cls: "bg-ma-ok-soft text-ma-ok-text", title: "Read from org" },
  C: { cls: "bg-ma-info-soft text-ma-info-text", title: "Created / generated" },
  M: { cls: "bg-ma-warn-soft text-ma-warn-text", title: "Modified" },
} as const;

export function MultiAgentWorkspacePanel({
  disabled,
  onMoveUp,
  onMoveDown,
}: {
  disabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  // Accepted for call-site compatibility; workspace files open via their own
  // artifact content (see `open`), not the metadata-item path.
  onOpenFileInEditor?: (file: { id: string; name: string; metadataType?: string }) => void;
}) {
  const artifacts = useMultiAgentStore((s) => s.artifacts);
  const badges = useMultiAgentIdeStore((s) => s.artifactBadges);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const files = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; name: string; metadataType: string; badge?: keyof typeof _BADGE }[] = [];
    for (const a of artifacts) {
      // Only show files we can actually OPEN in Monaco + that exist in our
      // database: a code-type artifact with a real backend UUID. Anything else
      // (non-UUID/ephemeral ids, non-code types) is hidden, not just unopenable.
      if (!CODE_ARTIFACT_TYPES.has((a.type || "").toLowerCase()) || dismissed.has(a.id)) continue;
      if (!_isBackendUuid(a.id)) continue;
      const name = _bareName(a.name || "");
      if (!name || seen.has(name)) continue;
      seen.add(name);
      const metadataType = (a.type || "").toLowerCase() === "apex_trigger" ? "ApexTrigger" : "ApexClass";
      out.push({ id: a.id, name, metadataType, badge: badges[a.id] as keyof typeof _BADGE });
    }
    return out;
  }, [artifacts, badges, dismissed]);

  const open = async (f: { id: string; name: string; metadataType: string; badge?: keyof typeof _BADGE }) => {
    try {
      // Workspace files are run-produced ARTIFACTS with BARE-UUID ids. Their
      // content resolves via /api/artifacts/{id}; the metadata-item-by-id path
      // 404s for these (dev-agent never hit it — its artifact ids are `art_…`).
      // Fetch the generated content and seed the editor tab directly, so the
      // file opens with NO metadata probe and NO 404. The editor treats known
      // run-artifact ids as virtual (see useMetadataEditorPanel) so it won't
      // re-fetch; getMetadataItem still has an artifact fallback as a safety net.
      const art = await getArtifact(f.id);
      const code = extractArtifactCode(art.content || "");
      // The editor bridge silently bails when content is empty (it guards on
      // `!workspaceCodeRequest.content`), which looked like "nothing happens".
      // Surface it instead of failing invisibly.
      if (!code.trim()) {
        toast.error(`"${f.name}" has no source content to open.`);
        return;
      }
      useMultiAgentIdeStore.getState().setWorkspaceCodeRequest({
        id: f.id,
        name: f.name,
        content: code,
        metadataType: f.metadataType,
        badge: f.badge ?? "M",
      });
    } catch (e) {
      // Was a silent console.warn — a failed fetch/auth here is exactly the
      // "I click a source and nothing happens" symptom. Make it visible.
      console.warn("[multi-agent workspace-open] failed", e);
      toast.error(`Couldn't open ${f.name}: ${(e as Error)?.message ?? "fetch failed"}`);
    }
  };

  return (
    <PanelSection
      title="Workspace"
      icon={<Package className="w-3.5 h-3.5" />}
      count={files.length}
      disabled={disabled}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onClear={files.length ? () => setDismissed(new Set(artifacts.map((a) => a.id))) : undefined}
    >
      {files.length > 0 ? (
        <div className="space-y-1">
          {files.map((f) => {
            const b = f.badge ? _BADGE[f.badge] : null;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => void open(f)}
                disabled={disabled}
                className="flex w-full items-start gap-2 rounded px-1 py-1.5 text-left transition-colors hover:bg-muted/50 disabled:opacity-40"
              >
                {b ? (
                  <span className={`mt-0.5 shrink-0 rounded px-1 text-[11px] font-bold ${b.cls}`} title={b.title}>
                    {f.badge}
                  </span>
                ) : (
                  <span className="mt-0.5 h-4 w-4 shrink-0 rounded bg-muted" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{f.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{f.metadataType}</div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="py-2 text-center text-muted-foreground">No files in the workspace yet</div>
      )}
    </PanelSection>
  );
}
